import React, { PureComponent, useEffect, useRef, useState } from 'react';
import { DateFormat, DayOfWeek, Formats } from 'shovel-lib/types';
import { LargeText, MediumText, MiniText, RegularText, SmallText } from '@utils/typography';
import t from '@i18n/t';
import { Column, Row } from '../../common/layoutUtils';
import TippyTooltip from '../../common/tooltip/TippyTooltip';
import MaterialIcon from '../../common/icons/MaterialIcon';
import colors from '@utils/colors';
import { DatetimeInput } from '../../common/inputs';
import styled, { keyframes, withTheme } from 'styled-components';
import { useLocation } from 'react-router-dom';
import RadioButton from '../../common/radioButton/RadioButton';
import NumberInput from '../../common/inputs/number/NumberInput';
import Toggle from '../../common/Toggle';
import { now } from 'shovel-lib/utils/timeUtils';
import { PERSONALIZATION_Z_INDEX } from './constants';
import FirefoxErrorBoundary from '@components/common/error/FirefoxErrorBoundary';
import InlineDropDownInput from '@components/common/inputs/dropdown/InlineDropDownInput';
import { DueDot } from '@components/calendar/DueDot';
import { calendarStylingOptions } from '@utils/calendar/calendarUtils';
import Preference from '@components/settings/preferences/Preference';
import FreeTimeColorPicker from '@components/settings/preferences/FreeTimeColorPicker';
import BackgroundPicker from '@components/settings/preferences/BackgroundPicker';
import storage from '@utils/storage';
import { LATE_START_WAYS, savedLateStart } from '@utils/timer';
import {
  DarkHours,
  fromTimeFieldValue,
  SCROLL_TO_APPEARANCE,
  ThemePreference,
  toTimeFieldValue
} from '@utils/theme/themePreference';
import { useThemeMode } from '@utils/theme/themeMode';
import {
  BUMPERS_MODES,
  bumpersMode,
  repeatsDroppedCommitments,
  usesBumpers,
  showsMarkUsedButtons,
  showsStudyStreak,
  showsStudyStreakDays,
  showsToStudyLabel,
  startsAheadByTimeNeeded
} from '@utils/settingsUtils';

const dayOptions = [
  { value: DayOfWeek.TUESDAY, label: t.TUESDAY },
  { value: DayOfWeek.WEDNESDAY, label: t.WEDNESDAY },
  { value: DayOfWeek.THURSDAY, label: t.THURSDAY },
  { value: DayOfWeek.FRIDAY, label: t.FRIDAY },
  { value: DayOfWeek.SATURDAY, label: t.SATURDAY }
];

type Props = {
  values: any;
  setFieldValue: any;
  dayOfWeekInDropdown: any;
  isDialog?: boolean;
  onChange?: any;
  title?: string;
  theme: any;
};

type State = { dayOfWeekDropdownValue: DayOfWeek };

class PersonalizationContent extends PureComponent<Props, State> {
  readonly state = {
    dayOfWeekDropdownValue:
      !!this.props.values.firstDayOfWeek &&
      //@ts-ignore
      this.props.values.firstDayOfWeek !== DayOfWeek.SUNDAY &&
      this.props.values.firstDayOfWeek !== DayOfWeek.MONDAY
        ? this.props.values.firstDayOfWeek
        : DayOfWeek.SATURDAY
  };

  handleFieldChange = (fieldName, value) => {
    const { setFieldValue, isDialog, onChange, values } = this.props;
    setFieldValue(fieldName, value);
    if (!isDialog) {
      onChange?.(values);
    }
  };

  renderRadioButtonSections = () => {
    const { dayOfWeekDropdownValue } = this.state;
    const { values, isDialog, dayOfWeekInDropdown } = this.props;
    const dateFormatPreview = now();

    return (
      <RadioButtonSection>
        <RadioButtonWrapper isDialog={isDialog}>
          <Heading>{t.TIME_FORMAT}</Heading>
          <Radio
            label={`12 ${t.HOUR}`}
            onChange={() => this.handleFieldChange('time24Format', false)}
            checked={!values.time24Format}
          />
          <Radio
            label={`24 ${t.HOUR}`}
            onChange={() => this.handleFieldChange('time24Format', true)}
            checked={!!values.time24Format}
          />
        </RadioButtonWrapper>
        <RadioButtonWrapper isDialog={isDialog}>
          <Heading>{t.DATE_FORMAT}</Heading>
          {Object.values(DateFormat).map(value => (
            <Radio
              key={value}
              label={dateFormatPreview.format(value)}
              onChange={() => this.handleFieldChange('dateFormat', value)}
              checked={values.dateFormat === value}
            />
          ))}
        </RadioButtonWrapper>
        <RadioButtonWrapper isDialog={isDialog}>
          <Heading>{t.START_WEEK_ON}</Heading>
          <Radio
            label={t.SUNDAY}
            onChange={() => this.handleFieldChange('firstDayOfWeek', DayOfWeek.SUNDAY)}
            checked={values.firstDayOfWeek === DayOfWeek.SUNDAY}
          />
          <Radio
            label={t.MONDAY}
            onChange={() => this.handleFieldChange('firstDayOfWeek', DayOfWeek.MONDAY)}
            checked={values.firstDayOfWeek === DayOfWeek.MONDAY}
          />
          <RepeatOptionsWrapper>
            <Radio
              label={`Custom ${dayOfWeekInDropdown ? 'on' : ''}`}
              checked={dayOfWeekInDropdown}
              onChange={() => this.handleFieldChange('firstDayOfWeek', dayOfWeekDropdownValue)}
            />
            {dayOfWeekInDropdown && (
              <CustomInputWrapper>
                <InlineDropDownInput
                  initialValue={dayOfWeekDropdownValue}
                  onChange={value => {
                    this.setState({ dayOfWeekDropdownValue: value });
                    this.handleFieldChange('firstDayOfWeek', value);
                  }}
                  options={dayOptions}
                />
              </CustomInputWrapper>
            )}
          </RepeatOptionsWrapper>
        </RadioButtonWrapper>
      </RadioButtonSection>
    );
  };

  renderSwitch = (label: React.ReactNode, active: boolean, onChange: () => void, disabled?: boolean) => (
    <SettingToggle
      size="md"
      active={active}
      onChange={onChange}
      disabled={disabled}
      customLabel={<SettingText className="setting-label">{label}</SettingText>}
    />
  );

  render() {
    const { values, isDialog, title, theme } = this.props;
    const calendarStyles = calendarStylingOptions(theme);

    return (
      <Container isDialog={isDialog}>
        {title && <Title>{title}</Title>}
        {/* Grouped by what each setting changes (Petr, 2026-10-10): the look, the calendar, planning, dates. */}
        <GroupTitle>{t.PERSONALIZATION_LOOK}</GroupTitle>
        {!isDialog && <AppearanceSection />}
        <Section>
          <Heading>{t.CHOOSE_COLOR_SCHEME}</Heading>
          <Preference />
        </Section>
        <Section>
          <Heading>{t.FREE_TIME_COLOR}</Heading>
          <FreeTimeColorPicker />
        </Section>
        <Section>
          <Heading>{t.BACKGROUND}</Heading>
          <BackgroundPicker />
        </Section>
        <GroupTitle>{t.PERSONALIZATION_CALENDAR}</GroupTitle>
        <Section>
          <Heading>{t.CALENDAR_STYLING}</Heading>
          <Row style={{ marginTop: 10 }}>
            <EventType>
              <RegularText>
                {t.COURSE} {t.EVENTS}
              </RegularText>
              <Toggle
                onChange={() => this.handleFieldChange('courseEventStyle.sidebar', !values.courseEventStyle.sidebar)}
                active={values.courseEventStyle.sidebar}
                label={t.SIDEBAR}
                size="md"
              />
            </EventType>
            {Object.entries(calendarStyles).map(([key, option]) => (
              <OptionNameWrapper key={key}>
                <RegularText>{option.name}</RegularText>
                <CalendarEventSample
                  key={key}
                  color={'#1976D2'}
                  style={{ borderColor: 'rgba(25, 118, 210, 0.3)' }}
                  styles={option}
                >
                  <MiniText color={'#1976D2'}>3-6pm</MiniText>
                  <SmallText>{t.COURSE_EVENT_TITLE_EXAMPLE}</SmallText>
                  <CalendarRadio
                    checked={key === values.courseEventStyle.style}
                    onChange={() => this.handleFieldChange('courseEventStyle.style', key)}
                  />
                  {values.courseEventStyle.sidebar && <BorderLeft color={'#1976D2'} />}
                </CalendarEventSample>
              </OptionNameWrapper>
            ))}
          </Row>
          <Row>
            <EventType>
              <RegularText>
                {t.ACTIVITY} {t.EVENTS}
              </RegularText>
              <Toggle
                onChange={() => this.handleFieldChange('activityEventStyle.sidebar', !values.activityEventStyle.sidebar)}
                active={values.activityEventStyle.sidebar}
                label={t.SIDEBAR}
                size="md"
              />
            </EventType>
            {Object.entries(calendarStyles).map(([key, option]) => (
              <CalendarEventSample
                key={key}
                color={'#F57C00'}
                style={{ borderColor: 'rgba(245, 124, 0, 0.3)' }}
                styles={option}
              >
                <MiniText color={'#F57C00'}>3-6pm</MiniText>
                <SmallText>{t.ACTIVITY_EVENT_TITLE_EXAMPLE}</SmallText>
                <CalendarRadio
                  checked={key === values.activityEventStyle.style}
                  onChange={() => this.handleFieldChange('activityEventStyle.style', key)}
                />
                {values.activityEventStyle.sidebar && <BorderLeft color={'#F57C00'} />}
              </CalendarEventSample>
            ))}
          </Row>
          <Row>
            <EventType>
              <RegularText>{t.GOOGLE_EVENTS}</RegularText>
              <Toggle
                onChange={() => this.handleFieldChange('googleEventStyle.sidebar', !values.googleEventStyle.sidebar)}
                active={values.googleEventStyle.sidebar}
                label={t.SIDEBAR}
                size="md"
              />
            </EventType>
            {Object.entries(calendarStyles).map(([key, option]) => (
              <CalendarEventSample
                key={key}
                color={'#5873FF'}
                style={{ borderColor: 'rgba(88, 115, 255, 0.3)' }}
                styles={option}
              >
                <MiniText color={'#5873FF'}>3-6pm</MiniText>
                <SmallText>{t.GOOGLE_EVENT_TITLE_EXAMPLE}</SmallText>
                <CalendarRadio
                  checked={key === values.googleEventStyle.style}
                  onChange={() => this.handleFieldChange('googleEventStyle.style', key)}
                />
                <LockIcon name={'lock'} size={9} strong />
                {values.googleEventStyle.sidebar && <BorderLeft color={'#5873FF'} />}
              </CalendarEventSample>
            ))}
          </Row>
          <Row>
            <EventType>
              <RegularText>{t.PLANNED_TASKS}</RegularText>
              <Toggle
                onChange={() =>
                  this.handleFieldChange('plannedTaskEventStyle.sidebar', !values.plannedTaskEventStyle.sidebar)
                }
                active={values.plannedTaskEventStyle.sidebar}
                label={t.SIDEBAR}
                size="md"
              />
            </EventType>
            {Object.entries(calendarStyles).map(([key, option]) => (
              <CalendarEventSample
                key={key}
                color={'#1AB77F'}
                style={{ borderColor: 'rgba(26, 183, 127, 0.3)' }}
                styles={option}
              >
                <MiniText color={'#1AB77F'}>3-6pm</MiniText>
                <SmallText>{t.PLANNED_TASK_TITLE_EXAMPLE}</SmallText>
                <SmallText style={{ fontWeight: 500, lineHeight: 1 }}>{t.PLANNED_TASK_SUBTITLE_EXAMPLE}</SmallText>
                <CalendarRadio
                  checked={key === values.plannedTaskEventStyle.style}
                  onChange={() => this.handleFieldChange('plannedTaskEventStyle.style', key)}
                />
                {values.plannedTaskEventStyle.sidebar && <BorderLeft color={'#1AB77F'} />}
              </CalendarEventSample>
            ))}
          </Row>
        </Section>
        <Section>
          <Heading>{t.DEADLINE_FLAGS_TITLE}</Heading>
          {this.renderSwitch(
            <>
              {t.SHOW_DEADLINE_FLAGS} <DueDot style={{ marginLeft: 4 }} />
            </>,
            !!values.showFlags,
            () => this.handleFieldChange('showFlags', !values.showFlags)
          )}
        </Section>
        <Section>
          <Heading>{t.SHOW_COMPLETED_TASKS_PERSONALIZATION_TITLE}</Heading>
          {this.renderSwitch(t.AT_TOP_OF_CALEDNAR, values.showCompletedTasks.calendarHeader, () =>
            this.handleFieldChange('showCompletedTasks.calendarHeader', !values.showCompletedTasks.calendarHeader)
          )}
          {this.renderSwitch(
            t.IN_DEADLINE_FLAGS,
            values.showCompletedTasks.flags,
            () => this.handleFieldChange('showCompletedTasks.flags', !values.showCompletedTasks.flags),
            !values.showFlags
          )}
        </Section>
        <Section>
          <Heading>{t.FREE_STUDY_TIME_TITLE}</Heading>
          {/* Through the helper: a missing key means off, for accounts from before the setting. */}
          {this.renderSwitch(t.SHOW_TO_STUDY_LABEL, showsToStudyLabel(values), () =>
            this.handleFieldChange('showToStudyLabel', !showsToStudyLabel(values))
          )}
        </Section>
        {/* The ✓ disc ships with Auto-plan, to every student. Through the helper: a missing key still means on, so
            flipping the raw value saved "on" again and the switch never turned off. */}
        <Section>
          <Heading>Past planned sessions</Heading>
          {this.renderSwitch('Show a ✓ to log time', showsMarkUsedButtons(values), () =>
            this.handleFieldChange('showMarkUsedButtons', !showsMarkUsedButtons(values))
          )}
        </Section>
        <Section>
          <Heading>{t.SHOW_STUDY_STREAK_PERSONALIZATION_TITLE}</Heading>
          {/* Through the helpers: the dialog passes the raw settings, where a missing key falls back to this browser. */}
          {this.renderSwitch(t.SHOW_STUDY_STREAK_PERSONALIZATION_TEXT, showsStudyStreak(values), () => {
            const studyStreakOn = !showsStudyStreak(values);
            this.handleFieldChange('showStudyStreak', studyStreakOn);
            // Builds from before the setting was saved read only this browser's choice.
            storage.setShowStudyStreakOption(studyStreakOn);
          })}
          {this.renderSwitch(
            t.SHOW_STUDY_STREAK_DAYS_TEXT,
            showsStudyStreakDays(values),
            () => this.handleFieldChange('showStudyStreakDays', !showsStudyStreakDays(values)),
            !showsStudyStreak(values)
          )}
        </Section>
        <GroupTitle>{t.PERSONALIZATION_PLANNING}</GroupTitle>
        <Section>
          <Heading>{t.START_AHEAD_FOR_TASKS}</Heading>
          {/* A new task's Start ahead once it has a due date: the same days for every task, or worked out from its time
              needed - those days until it has an estimate (Petr). */}
          <Row>
            <Radio
              label={t.START_AHEAD_SET_DAYS}
              // Saved as 'days', not dropped: a missing key leaves the saved choice in place.
              onChange={() => this.handleFieldChange('startAheadMode', 'days')}
              checked={!startsAheadByTimeNeeded(values)}
            />
            <DaysAheadInput
              noErrorMargin
              inline
              autosize
              //@ts-ignore
              value={values.daysAhead}
              onChange={e => this.handleFieldChange('daysAhead', e.target.value)}
              min={0}
            />
            <TippyTooltip
              target={
                <Row>
                  <MaterialIcon name={'help'} size={20} color={colors.primaryPurple} />
                </Row>
              }
              content={t.DEFAULT_START_AHEAD_TOOLTIP}
              preformattedText
              zIndex={PERSONALIZATION_Z_INDEX + 1}
            />
          </Row>
          <Row>
            <Radio
              label={t.START_AHEAD_BY_TIME_NEEDED}
              onChange={() => this.handleFieldChange('startAheadMode', 'time')}
              checked={startsAheadByTimeNeeded(values)}
            />
            <TippyTooltip
              target={
                <Row style={{ marginLeft: 5 }}>
                  <MaterialIcon name={'help'} size={20} color={colors.primaryPurple} />
                </Row>
              }
              content={t.START_AHEAD_BY_TIME_NEEDED_TOOLTIP}
              preformattedText
              zIndex={PERSONALIZATION_Z_INDEX + 1}
            />
          </Row>
        </Section>
        <Section>
          <Heading>{t.REPEAT_DROPPED_COMMITMENTS_TITLE}</Heading>
          {/* Through the helper: the dialog passes the raw settings, where a missing key still means on. */}
          {this.renderSwitch(t.REPEAT_DROPPED_COMMITMENTS_TEXT, repeatsDroppedCommitments(values), () =>
            this.handleFieldChange('repeatDroppedCommitments', !repeatsDroppedCommitments(values))
          )}
        </Section>
        <Section>
          <Heading>{t.BUMPERS}</Heading>
          {/* Through the helper: a missing key means on. The calendar's Bumpers switch saves the same setting. */}
          {this.renderSwitch(t.BUMPERS_TEXT, usesBumpers(values), () =>
            this.handleFieldChange('bumpers', !usesBumpers(values))
          )}
          {/* What happens to the sessions a drag passes; the calendar's Bumpers menu saves the same (Petr, 2026-10-28). */}
          {usesBumpers(values) && (
            <BumpersModes>
              <SettingText>{t.BUMPERS_MODE_TITLE}</SettingText>
              {BUMPERS_MODES.map(mode => (
                <Radio
                  key={mode}
                  label={t.BUMPERS_MODE_OPTIONS[mode]}
                  checked={bumpersMode(values) === mode}
                  onChange={() => this.handleFieldChange('bumpersMode', mode)}
                />
              ))}
              <MiniText>{t.BUMPERS_SHIFT_HINT(bumpersMode(values))}</MiniText>
            </BumpersModes>
          )}
        </Section>
        <Section>
          <Heading>{t.LATE_START_TITLE}</Heading>
          {/* What ▶ on a late session does by default (bump.ts); the timer's Change still offers the others. */}
          <LateStartOptions>
            {LATE_START_WAYS.map(way => (
              <Radio
                key={way}
                label={t.LATE_START_OPTIONS[way]}
                checked={savedLateStart(values) === way}
                onChange={() => this.handleFieldChange('lateStart', way)}
              />
            ))}
          </LateStartOptions>
        </Section>
        <GroupTitle>{t.PERSONALIZATION_DATE_TIME}</GroupTitle>
        {this.renderRadioButtonSections()}
      </Container>
    );
  }
}

export default withTheme(PersonalizationContent);

const LateStartOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BumpersModes = styled(LateStartOptions)`
  margin-top: 12px;
`;

export const Container = styled.div<{ isDialog?: boolean }>`
  flex: 1;
  // Room under the last setting, so it scrolls up clear of the bottom edge and the chat button (Petr, 2026-10-10).
  ${props => (!props.isDialog ? `padding: 20px 30px 160px` : `padding: 0px 45px`)};
  overflow: auto;
`;

// Headings are bigger, bolder and darker than the settings under them, and not in capitals, so the two don't blend.
export const Heading = styled(RegularText)`
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: ${props => props.theme.textStrongColor};
  margin: 0 0 10px;
`;

// The settings themselves: lighter than the headings.
export const SettingText = styled(RegularText)`
  font-size: 14px;
  font-weight: 500;
`;

// One row per switch: its text on the left, the switches lined up on the right.
export const SettingToggle = styled(Toggle)`
  justify-content: space-between;
  max-width: 580px;
  min-height: 32px;

  .setting-label {
    margin-right: 20px;
    display: flex;
    align-items: center;

    .emoji-mart-emoji {
      margin-left: 5px;
      display: flex;
    }
  }
`;

// The four groups the settings sit in: bigger again than the headings, with room above to start afresh.
const GroupTitle = styled(LargeText)`
  display: block;
  font-size: 19px;
  font-weight: 700;
  color: ${props => props.theme.textStrongColor};
  margin: 40px 0 8px;
  &:first-of-type {
    margin-top: 4px;
  }
`;

export const Section = styled(Column)<{ last?: boolean }>`
  border-top: 1px solid ${props => props.theme.borderColor};
  padding: 18px 0px 20px 0px;
  box-sizing: border-box;
  ${props => props.last && `border-bottom: 1px solid ${props => props.theme.borderColor}`};
`;

const Radio = styled(RadioButton)``;

// With the sidebar popup's icons (Petr, 2026-10-11).
const THEME_OPTIONS: { preference: ThemePreference; label: string; icon: string }[] = [
  { preference: 'light', label: t.LIGHT, icon: 'light_mode' },
  { preference: 'dark', label: t.DARK, icon: 'dark_mode' },
  { preference: 'auto', label: t.AUTO_THEME_OPTION, icon: 'brightness_auto' },
  { preference: 'scheduled', label: t.SET_HOURS, icon: 'schedule' }
];

/**
 * Light, dark, Auto following the device, or set hours: the sidebar's button offers the same (Petr, 2026-10-08), and
 * sends students here to change the hours. Kept in this browser rather than with the other settings, so it applies at
 * once and isn't saved with them; for that reason it is left out of the dialog, which saves only on submit.
 */
const AppearanceSection = () => {
  const { preference, darkHours, setPreference, setDarkHours } = useThemeMode();
  const location = useLocation<{ scrollTo?: string } | undefined>();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [flashes, setFlashes] = useState(0);

  // Opened from the sidebar's "Change hours", the page comes to this section and lights it up (Petr: lost at the top of
  // a long page). Each time the link is followed, even from this page.
  useEffect(() => {
    if (location.state?.scrollTo !== SCROLL_TO_APPEARANCE) return;
    const frame = requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlashes(count => count + 1);
    });
    return () => cancelAnimationFrame(frame);
  }, [location.key, location.state]);

  // Changing an hour means wanting set hours, so it picks them too.
  const changeHour = (edge: keyof DarkHours, date: Date | null) => {
    if (!date) return;
    setDarkHours({ ...darkHours, [edge]: fromTimeFieldValue(date) });
    if (preference !== 'scheduled') setPreference('scheduled');
  };

  const hourInput = (edge: keyof DarkHours) => (
    <FirefoxErrorBoundary>
      <DatetimeInput
        noErrorMargin
        inline
        autosizeFormat={() => Formats.LOWERCASE_TIME}
        value={toTimeFieldValue(darkHours[edge])}
        onChange={date => changeHour(edge, date)}
        showTimeOnly
      />
    </FirefoxErrorBoundary>
  );

  return (
    <AppearanceWrapper ref={sectionRef}>
      {flashes > 0 && <Flash key={flashes} />}
      <RadioButtonWrapper>
        <Heading>{t.APPEARANCE}</Heading>
        {THEME_OPTIONS.map(option => (
          <Radio
            key={option.preference}
            label={option.label}
            icon={option.icon}
            onChange={() => setPreference(option.preference)}
            checked={preference === option.preference}
          />
        ))}
        <DarkHoursRow>
          <SettingText>{t.DARK_FROM}</SettingText>
          {hourInput('from')}
          <SettingText>{t.TO.toLowerCase()}</SettingText>
          {hourInput('to')}
        </DarkHoursRow>
      </RadioButtonWrapper>
    </AppearanceWrapper>
  );
};

const AppearanceWrapper = styled(Section)`
  position: relative;
`;

const flash = keyframes`
  from,
  30% {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

// A tint behind the section that fades away, wider than it so the radios aren't against its edge.
const Flash = styled.div`
  position: absolute;
  inset: 6px -12px;
  border-radius: 10px;
  background: ${({ theme }) => theme.primary}1f;
  pointer-events: none;
  opacity: 0;
  animation: ${flash} 1.8s ease-out;
`;

// Under Set hours, lined up with its label, past its icon.
const DarkHoursRow = styled(Row)`
  align-items: center;
  gap: 5px;
  margin-left: 54px !important;
`;

const RepeatOptionsWrapper = styled(Row)`
  align-items: center;
  margin-bottom: 20px;
  ${Radio} > span {
    color: ${props => props.theme.textColor};
  }
`;

const CustomInputWrapper = styled.div`
  margin-left: 10px;
`;

const DaysAheadInput = styled(NumberInput)`
  margin-left: 5px;
`;

const RadioButtonSection = styled(Row)`
  border-top: 1px solid ${props => props.theme.borderColor};
  padding: 18px 0px 20px 0px;
  box-sizing: border-box;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const RadioButtonWrapper = styled(Column)<{ isDialog?: boolean }>`
  > * {
    margin: 7px 0 0;
  }
  > ${Heading} {
    margin: 0 0 3px;
  }
  ${props =>
    !props.isDialog
      ? `margin-right: 50px;
     &:last-child {
       margin-right: 0px;
     }
    `
      : `width: 30%;
     &:last-child {
       width: 40%;
     } `}
`;

export const Title = styled(LargeText)`
  font-size: 24px;
  color: ${props => props.theme.textStrongColor};
  margin-bottom: 20px;
  display: block;
`;

const CalendarEventSample = styled(Column)<{ styles: any; color: string }>`
  position: relative;
  width: 100px;
  height: 60px;
  border: 1px solid;
  border-radius: 3px;
  padding: 2px 5px;
  padding-left: 7px;
  box-sizing: border-box;
  box-shadow: ${props => `0 2px 2px ${props.theme.boxShadow}, 0 0 2px ${props.theme.boxShadow}`};
  background-color: ${props => props.styles.backgroundFunction(props.color)};
  margin: 20px;
  margin-right: 0;
`;

const CalendarRadio = styled(RadioButton)`
  position: absolute;
  border-radius: 50%;
  top: -10px;
  right: -10px;
  background: ${props => props.theme.background};
  padding: 2px;
`;

const EventType = styled(Column)`
  width: 120px;
  ${RegularText} {
    text-transform: capitalize;
  }
  ${MediumText} {
    margin-left: 0;
    text-transform: capitalize;
  }
`;

const LockIcon = styled(MaterialIcon)`
  position: absolute;
  right: 1px;
  bottom: 1px;
`;

const BorderLeft = styled.div<{ color: string }>`
  position: absolute;
  top: -1px;
  left: -1px;
  bottom: -1px;
  width: 3px;
  background: ${props => props.color};
  border-radius: 3px 0 0 3px;
`;

const OptionNameWrapper = styled(Column)`
  position: relative;
  align-items: center;
  > ${RegularText} {
    position: absolute;
    top: -10px;
  }
`;
